/**
 * @fileoverview Constants used throughout the English Acquaintances Game
 * Centralizes configuration values for easier maintenance
 */

/**
 * Question types used in the game
 * Each question type corresponds to a property in the work objects
 */
export const QUESTION_TYPES = {
  AUTHOR: 'author',
  PUBLICATION_YEAR: 'publication_year',
  GENRE: 'genre',
  LITERARY_AGE: 'age'
};

/**
 * Subject codes available in the game
 */
export const SUBJECTS = {
  POST_INDEPENDENCE: '241',
  TRANSITION_DRYDEN_POPE: '242'
};

/**
 * Special validation rules for different subjects
 * Configures alternative acceptable answers for each dataset
 */
export const SPECIAL_VALIDATIONS = {
  // 241 dataset special rules
  [SUBJECTS.POST_INDEPENDENCE]: {
    // Post Independence Age specific validations
    'Post Independence Age': ['post independence', 'post independent', 'modern indian'],
    
    // Special author name validations for common Indian author names
    'A.K Ramanujan': ['ak ramanujan', 'a k ramanujan', 'ramanujan'],
    'Nissim Ezekiel': ['nissim', 'ezekiel'],
    'Purushottam Lal': ['purshottam lal', 'p lal'],
    'Shiv K Kumar': ['shiv kumar', 'shiv k', 'sk kumar'],
    'Jayanta Mahapatra': ['jayanta', 'mahapatra'],
    'Bhabani Bhattacharya': ['bhabani', 'bhattacharya'],
    'Manohar Malgonkar': ['manohar', 'malgonkar'],
    'Khushwant Singh': ['khushwant', 'singh'],
    'Girish Karnad': ['girish', 'karnad'],
    'R.K. Narayan': ['rk narayan', 'r k narayan', 'narayan'],
    'Nirad Chaudhuri': ['nirad', 'chaudhuri'],
    'Ruth Jhabvala': ['ruth', 'jhabvala', 'ruth prawer jhabvala'],
    'Arun Kolatkar': ['arun', 'kolatkar']
  },
  
  // 242 dataset special rules
  [SUBJECTS.TRANSITION_DRYDEN_POPE]: {
    // Accept simplified age names without the "Age of" prefix
    'Age of Transition': ['transition', 'transitional period'],
    'Age of Dryden': ['dryden', 'drydens age', 'john dryden'],
    'Age of Pope': ['pope', 'popes age', 'alexander pope']
  }
};

/**
 * Common variations for literary ages and genres
 * Used in the text comparison logic to provide flexibility in answers
 * Includes common typos, abbreviations, and alternative names
 */
export const COMMON_VARIATIONS = {
  // Literary Ages/Periods
  'modern': ['modern age', 'modern period', 'modern era', 'modernism', 'modernist'],
  'victorian': ['victorian age', 'victorian period', 'victorian era', 'victoria', 'victorians'],
  'romantic': ['romantic age', 'romantic period', 'romanticism', 'the romantics', 'romantic era'],
  'renaissance': ['renaissance period', 'renaissance age', 'elizabethan', 'elizabethan age', 'elizabethan era'],
  'medieval': ['medieval age', 'medieval period', 'middle ages', 'middle age', 'dark ages'],
  'post independence age': ['post independence', 'post independence period', 'post independence era', 'post independent'],
  'post independence': ['post independence age', 'post independence period', 'post independence era'],
  'age of transition': ['transition', 'transition period', 'transitional period'],
  'age of dryden': ['dryden', 'drydens age', 'age of john dryden'],
  'age of pope': ['pope', 'popes age', 'age of alexander pope'],
  
  // Genres
  'novel': ['fiction', 'prose fiction', 'fictional work', 'prose', 'narrative fiction'],
  'poem': ['verse', 'lyrics', 'poetry', 'poetic work', 'poetical work'],
  'drama': ['play', 'theatre', 'theater', 'stage play', 'dramatic work'],
  'collection of poems': ['anthology of poems', 'poetry collection', 'poem collection', 'collected poems', 'poetry anthology'],
  'collection of short stories': ['short story collection', 'anthology of stories', 'short stories', 'story collection'],
  'historical play': ['history play', 'historical drama', 'historical theatre'],
  'verse play': ['poetic drama', 'dramatic poem', 'verse drama'],
  'autobiography': ['memoir', 'personal narrative', 'life story', 'self biography'],
  'essays': ['essay collection', 'collected essays', 'collection of essays']
};

/**
 * Display labels for each question type shown in the UI
 */
export const DISPLAY_LABELS = {
  [QUESTION_TYPES.AUTHOR]: 'Writer',
  [QUESTION_TYPES.PUBLICATION_YEAR]: 'Publication Year',
  [QUESTION_TYPES.GENRE]: 'Genre',
  [QUESTION_TYPES.LITERARY_AGE]: 'Literary Age'
};

/**
 * Question prompts for each question type
 */
export const QUESTION_PROMPTS = {
  [QUESTION_TYPES.AUTHOR]: 'Who is the writer of this work?',
  [QUESTION_TYPES.PUBLICATION_YEAR]: 'In which year was this work published?',
  [QUESTION_TYPES.GENRE]: 'What is the genre of this work?',
  [QUESTION_TYPES.LITERARY_AGE]: 'To which literary age does this work belong?'
};

/**
 * Game messages for different scenarios
 */
export const GAME_MESSAGES = {
  VICTORY: "Congratulations! You've mastered all 15 works and completed the English Acquaintances Game!",
  FAILURE: "Sorry! Your answer was incorrect. You need to identify all 15 works correctly to win.",
  IN_PROGRESS: "Keep going! You need to correctly identify all 15 works to win the game.",
  NEW_WORK: "Now try to identify this new literary work correctly."
}; 